<script setup>
import { usePageHead } from '@composables/use-page-head'
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import Layout from '@layouts/login'
import localSave from '@utils/local-save'
import { useApi } from '@composables/use-api'
import { email as emailValidator, required, useFormValidation } from '@composables/use-form-validation'
import { useAppearanceStore } from '@state/stores/appearance'
import { useMessages } from '@composables/use-messages'

const getMessageText = useMessages()
const router = useRouter()
const appearanceStore = useAppearanceStore()
const email = ref('')
const checking = ref(false)
const validation = useFormValidation({
    email: {
        value: email,
        validators: [required, emailValidator],
    },
})

function notify(messageKey, type) {
    appearanceStore.handleNotification({
        message: getMessageText(messageKey),
        type,
    })
}

function redirect() {
    const redirectURL = localSave.get('redirectURL') || '/'
    router.push({ path: redirectURL })
}

function clearFields() {
    email.value = ''
    validation.$reset()
}

function handleSuccess() {
    notify('EMAIL_RESET_LINK_SENT', 'success')
    redirect()
}

function handleError(error) {
    if (error.response.status === 404) {
        notify('EMAIL_NOT_FOUND', 'danger')
        clearFields()
    } else {
        notify('SOMETHING_WENT_WRONG', 'danger')
    }
    checking.value = false
}

function requestPasswordReset() {
    if (validation.$invalid) {
        notify('EMAIL_REQUIRED', 'warning')
        validation.$reset()
        return undefined
    }

    checking.value = true
    return useApi()
        .members.resetPasswordRequest({ email: email.value }, { email: email.value })
        .then(handleSuccess)
        .catch(handleError)
}

usePageHead({ title: 'Request Password Reset', description: 'Request Password Reset' })
</script>

<template>
    <Layout>
        <BaseContent>
            <BaseSection padded>
                <div class="password-reset-form-container">
                    <h4 class="password-reset-title">Password Reset</h4>
                    <h6 class="password-reset-subtitle">
                        Enter your email that you used to register with FRISKY. We'll send you an email with a link to
                        reset your password.
                    </h6>
                    <form
                        class="password-reset-form"
                        @submit.prevent="requestPasswordReset"
                    >
                        <div class="password-reset-field-container">
                            <BaseInput
                                v-model="email"
                                :class="[
                                    'password-reset-field',
                                    validation.email.$error && 'password-reset-error',
                                ]"
                                name="email"
                                autocomplete="email"
                                placeholder="email*"
                                @blur="validation.email.$touch()"
                            />
                            <label
                                v-if="validation.email.$error"
                                :class="['password-reset-label', 'password-reset-error']"
                            >This field is required and must be an email</label>
                        </div>
                        <BaseButton
                            class="password-reset-button"
                            :disabled="checking"
                            :modifiers="{ fill: true }"
                            button-type="highlight"
                            type="submit"
                            @click="() => {}"
                        >Send</BaseButton>
                    </form>
                </div>
            </BaseSection>
        </BaseContent>
    </Layout>
</template>

<style lang="scss" scoped>
@import '@design';

.password-reset-form-container {
    position: relative;
    margin-bottom: $s-h;
    .password-reset-field {
        &.password-reset-error {
            margin-bottom: $s-xl;
            border-color: $c-status-error-text;
            border-width: 2px;
        }
    }
}
.password-reset-field-container {
    position: relative;
    width: 100%;
}
.password-reset-form {
    display: flex;
    flex-direction: column;
    align-items: center;
}
.password-reset-label {
    @extend %contentSubtitleM;

    display: block;
    font-weight: 300;
    text-align: left;

    &.password-reset-error {
        position: absolute;
        bottom: $s-xs;
        color: $c-status-error-text;
    }
}
.password-reset-title {
    text-align: left;
}
.password-reset-subtitle {
    @extend %contentTinyHeading;

    font-weight: 400;
    text-align: left;
}

.password-reset-button {
    margin-top: $s-xl;
}
</style>
